import { useEffect, useMemo, useState } from 'react'
import { useLiveItems } from '../store/index.js'
import { getServerNowMs, useServerClock } from './useServerClock.js'
import type { ScheduleItem } from '../types/index.js'

export interface OnAirState {
  /** Item réellement à l'antenne, ou celui qui devrait l'être selon l'horloge serveur. */
  onAirItem: ScheduleItem | null
  /** Prochain item programmé, hors éléments passés, sautés ou déjà à l'antenne. */
  nextItem:  ScheduleItem | null
  /** Progression de l'item courant, 0 → 100. */
  progress:  number
  /** Secondes écoulées et restantes sur l'item courant. */
  elapsed:   number
  remaining: number
  /** Secondes avant le démarrage de l'item suivant, null s'il n'y en a pas. */
  untilNext: number | null
  /** Prochain direct programmé — le repère que l'opérateur anticipe le plus. */
  nextLive:  ScheduleItem | null
  untilNextLive: number | null
}

/**
 * État d'antenne partagé par le bandeau permanent et le rail de la Grille.
 *
 * L'horloge de référence est celle du backend, pas celle du navigateur : c'est
 * le backend qui pilote CasparCG, un poste désynchronisé afficherait un
 * décompte faux.
 *
 * Les items viennent de `useLiveItems` (jour d'antenne réel), pas du jour
 * consulté dans la Grille : sinon naviguer vers un autre jour ou une autre
 * semaine ferait passer le bandeau permanent en « fin de grille ».
 */
export function useOnAirState(): OnAirState {
  const items = useLiveItems()
  const { nowMs } = useServerClock()

  const onAirItem = useMemo(() => {
    const explicitlyOnAir = items.find(item => item.status === 'ON_AIR')
    if (explicitlyOnAir) return explicitlyOnAir

    // Pas d'item ON_AIR : on retient celui que l'horloge serveur désigne.
    return items.find(item => {
      const start = new Date(item.startTime).getTime()
      const end   = new Date(item.endTime).getTime()
      return nowMs >= start && nowMs < end
    }) ?? null
  }, [items, nowMs])

  const nextItem = useMemo(() => {
    const sorted = [...items].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    )
    return sorted.find(item =>
      item.status !== 'DONE'
      && item.status !== 'SKIPPED'
      && item.status !== 'ON_AIR'
      && new Date(item.startTime).getTime() > nowMs,
    ) ?? null
  }, [items, nowMs])

  const [timings, setTimings] = useState({ progress: 0, elapsed: 0, remaining: 0 })

  // Tick propre à 1 s : la progression doit avancer même quand rien d'autre ne
  // change dans le store.
  useEffect(() => {
    if (!onAirItem) {
      setTimings({ progress: 0, elapsed: 0, remaining: 0 })
      return
    }

    const tick = () => {
      const now     = getServerNowMs()
      const startMs = new Date(onAirItem.startTime).getTime()
      const endMs   = new Date(onAirItem.endTime).getTime()
      const elapsed = Math.max(0, Math.round((now - startMs) / 1000))
      const remaining = Math.max(0, Math.round((endMs - now) / 1000))
      const duration = onAirItem.durationSec || Math.max(1, Math.round((endMs - startMs) / 1000))
      setTimings({
        elapsed,
        remaining,
        progress: Math.min(100, Math.max(0, Math.round((elapsed / duration) * 100))),
      })
    }

    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [onAirItem])

  const untilNext = useMemo(() => {
    if (!nextItem) return null
    return Math.max(0, Math.round((new Date(nextItem.startTime).getTime() - nowMs) / 1000))
  }, [nextItem, nowMs])

  // Un direct ne se rattrape pas : c'est l'échéance que l'opérateur surveille
  // le plus, bien avant l'élément immédiatement suivant.
  const nextLive = useMemo(() => {
    return [...items]
      .filter(item => item.itemType === 'LIVE'
        && item.status !== 'DONE' && item.status !== 'SKIPPED'
        && new Date(item.startTime).getTime() > nowMs)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0] ?? null
  }, [items, nowMs])

  const untilNextLive = useMemo(() => {
    if (!nextLive) return null
    return Math.max(0, Math.round((new Date(nextLive.startTime).getTime() - nowMs) / 1000))
  }, [nextLive, nowMs])

  return { onAirItem, nextItem, untilNext, nextLive, untilNextLive, ...timings }
}
